import { IApiSplitTicketItem } from 'features/ticketList/services/types/ticket';
import { roundToDecimal } from 'utils/roundToDecimal';

export type CategoryType = 'amount' | 'tip' | 'discount' | 'supplyFee';

export interface LockedField {
  staffIndex: number;
  category: CategoryType;
  value: number;
}

export interface AutoBalanceOptions {
  precision?: number;
  fallbackStaffIndex?: number;
}

/**
 * Tính toán auto-balance cho một category cụ thể
 * @param staffs - Danh sách staff hiện tại
 * @param category - Category cần balance (amount, tip, discount, supplyFee)
 * @param totalValue - Tổng giá trị của category
 * @param lockedFields - Danh sách các field đã bị khóa
 * @param options - Tùy chọn bổ sung
 * @returns Danh sách staff với giá trị đã được balance
 */
export function calculateAutoBalance(
  staffs: IApiSplitTicketItem[],
  category: CategoryType,
  totalValue: number,
  lockedFields: LockedField[],
  options: AutoBalanceOptions = {}
): IApiSplitTicketItem[] {
  const { fallbackStaffIndex = 0 } = options;

  if (staffs.length === 0) return staffs;

  const result = [...staffs];
  const categoryLocks = lockedFields.filter(
    (lock) => lock.category === category
  );

  // Tính tổng giá trị đã bị khóa
  const lockedTotal = categoryLocks.reduce((sum, lock) => {
    return sum + roundToDecimal(lock.value);
  }, 0);

  // Tính phần còn lại cần chia
  const remainingValue = roundToDecimal(totalValue - lockedTotal);

  // Lấy danh sách staff không bị khóa
  const unlockedStaffIndices = staffs
    .map((_, index) => index)
    .filter(
      (index) => !categoryLocks.some((lock) => lock.staffIndex === index)
    );

  if (unlockedStaffIndices.length === 0) {
    // Tất cả đều bị khóa, fallback: phân phần dư cho staff đầu tiên hoặc fallbackStaffIndex
    const targetIndex =
      fallbackStaffIndex < staffs.length ? fallbackStaffIndex : 0;
    result[targetIndex] = {
      ...result[targetIndex],
      [category]: roundToDecimal(
        (result[targetIndex][category] || 0) + remainingValue
      ),
    };
    return result;
  }

  // Chia đều phần còn lại cho các staff không bị khóa
  const baseShare = roundToDecimal(remainingValue / unlockedStaffIndices.length);
  
  // Áp dụng base share cho tất cả staff không bị khóa
  unlockedStaffIndices.forEach((index) => {
    result[index] = {
      ...result[index],
      [category]: roundToDecimal(baseShare),
    };
  });

  // Tính tổng thực tế sau khi chia
  const actualTotal = unlockedStaffIndices.reduce((sum, index) => {
    return sum + (result[index][category] || 0);
  }, 0);
  
  // Tính chênh lệch và điều chỉnh staff cuối cùng
  const difference = roundToDecimal(remainingValue - actualTotal);
  if (Math.abs(difference) > 0.001) {
    const lastIndex = unlockedStaffIndices[unlockedStaffIndices.length - 1];
    result[lastIndex] = {
      ...result[lastIndex],
      [category]: roundToDecimal((result[lastIndex][category] || 0) + difference),
    };
  }

  return result;
}

/**
 * Rebalance tất cả categories cho danh sách staff
 * @param staffs - Danh sách staff hiện tại
 * @param totals - Tổng giá trị của từng category
 * @param lockedFields - Danh sách các field đã bị khóa
 * @param options - Tùy chọn bổ sung
 * @returns Danh sách staff với tất cả categories đã được balance
 */
export function rebalanceAllCategories(
  staffs: IApiSplitTicketItem[],
  totals: Record<CategoryType, number>,
  lockedFields: LockedField[],
  options: AutoBalanceOptions = {}
): IApiSplitTicketItem[] {
  let result = [...staffs];

  // Balance từng category một cách độc lập
  const categories: CategoryType[] = ['amount', 'tip', 'discount', 'supplyFee'];

  categories.forEach((category) => {
    result = calculateAutoBalance(
      result,
      category,
      totals[category],
      lockedFields,
      options
    );
  });

  return result;
}

/**
 * Thêm lock cho một field cụ thể
 * @param lockedFields - Danh sách lock hiện tại
 * @param staffIndex - Index của staff
 * @param category - Category cần lock
 * @param value - Giá trị cần lock
 * @returns Danh sách lock mới
 */
export function addLock(
  lockedFields: LockedField[],
  staffIndex: number,
  category: CategoryType,
  value: number
): LockedField[] {
  const newLock: LockedField = {
    staffIndex,
    category,
    value: roundToDecimal(value),
  };

  // Loại bỏ lock cũ nếu có
  const filtered = lockedFields.filter(
    (lock) => !(lock.staffIndex === staffIndex && lock.category === category)
  );

  return [...filtered, newLock];
}

/**
 * Xóa lock cho một field cụ thể
 * @param lockedFields - Danh sách lock hiện tại
 * @param staffIndex - Index của staff
 * @param category - Category cần unlock
 * @returns Danh sách lock mới
 */
export function removeLock(
  lockedFields: LockedField[],
  staffIndex: number,
  category: CategoryType
): LockedField[] {
  return lockedFields.filter(
    (lock) => !(lock.staffIndex === staffIndex && lock.category === category)
  );
}

/**
 * Xóa tất cả locks cho một staff
 * @param lockedFields - Danh sách lock hiện tại
 * @param staffIndex - Index của staff
 * @returns Danh sách lock mới
 */
export function removeAllLocksForStaff(
  lockedFields: LockedField[],
  staffIndex: number
): LockedField[] {
  return lockedFields.filter((lock) => lock.staffIndex !== staffIndex);
}

/**
 * Cập nhật index của locks sau khi remove staff
 * @param lockedFields - Danh sách lock hiện tại
 * @param removedIndex - Index của staff bị xóa
 * @returns Danh sách lock mới với index đã được cập nhật
 */
export function updateLockIndicesAfterRemove(
  lockedFields: LockedField[],
  removedIndex: number
): LockedField[] {
  return lockedFields
    .filter((lock) => lock.staffIndex !== removedIndex)
    .map((lock) => ({
      ...lock,
      staffIndex:
        lock.staffIndex > removedIndex ? lock.staffIndex - 1 : lock.staffIndex,
    }));
}

/**
 * Lấy giá trị lock cho một field cụ thể
 * @param lockedFields - Danh sách lock hiện tại
 * @param staffIndex - Index của staff
 * @param category - Category cần kiểm tra
 * @returns Giá trị lock hoặc null nếu không có lock
 */
export function getLockedValue(
  lockedFields: LockedField[],
  staffIndex: number,
  category: CategoryType
): number | null {
  const lock = lockedFields.find(
    (lock) => lock.staffIndex === staffIndex && lock.category === category
  );
  return lock ? lock.value : null;
}

/**
 * Kiểm tra xem một field có bị lock không
 * @param lockedFields - Danh sách lock hiện tại
 * @param staffIndex - Index của staff
 * @param category - Category cần kiểm tra
 * @returns true nếu field bị lock
 */
export function isFieldLocked(
  lockedFields: LockedField[],
  staffIndex: number,
  category: CategoryType
): boolean {
  return lockedFields.some(
    (lock) => lock.staffIndex === staffIndex && lock.category === category
  );
}
